import React, { useState, useEffect } from 'react';
import CookieConsent from 'react-cookie-consent';
import { useTranslation } from 'react-i18next';
import { Box, Link } from '@mui/material';
import { updateConsent } from '@utils/analytics';

export const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if consent has already been given. Guarded like the rest of the
    // repo's storage access (popularEmojis.ts, skinToneUtils.ts): in private
    // mode / blocked-storage environments getItem can throw — treat that
    // exactly like absent consent (banner shows, consent state untouched).
    let consent: string | null = null;
    try {
      consent = localStorage.getItem('cookie-consent');
    } catch (error) {
      console.warn('Failed to read cookie consent from localStorage:', error);
    }
    if (!consent) {
      // First-time visitor: show the banner and do NOT touch consent state —
      // the GDPR/CCPA 'denied' default from public/index.html stays in force.
      setShowBanner(true);
      return;
    }

    // Returning visitor: re-apply the stored decision on every page load.
    // gtag's consent state is per-pageload (the page default is 'denied'), so
    // without this, returning ACCEPTED users silently stay denied forever —
    // updateConsent(true) previously only ever ran at banner-click time.
    // Safe before the async gtag script loads: the inline index.html snippet
    // defines window.gtag synchronously as a dataLayer queue push.
    if (consent === 'accepted') {
      updateConsent(true);
    } else if (consent === 'declined') {
      // Explicit, though it matches the page default.
      updateConsent(false);
    }
  }, []);

  const handleAccept = () => {
    // If persistence fails (private mode / blocked storage), consent still
    // applies for THIS session via updateConsent; the banner will simply
    // reappear on the next visit (nothing stored) — acceptable behavior.
    try {
      localStorage.setItem('cookie-consent', 'accepted');
    } catch (error) {
      console.warn('Failed to persist cookie consent to localStorage:', error);
    }
    setShowBanner(false);

    updateConsent(true);
  };

  const handleDecline = () => {
    try {
      localStorage.setItem('cookie-consent', 'declined');
    } catch (error) {
      console.warn('Failed to persist cookie consent to localStorage:', error);
    }
    setShowBanner(false);

    updateConsent(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <CookieConsent
      location="bottom"
      buttonText={t('cookieConsent.accept', 'Accept')}
      declineButtonText={t('cookieConsent.decline', 'Decline')}
      cookieName="getemoji-cookie-consent"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 -2px 16px rgba(0, 0, 0, 0.2)',
        padding: '12px 16px',
        alignItems: 'center',
      }}
      buttonStyle={{
        background: '#ffffff',
        color: '#667eea',
        fontSize: '14px',
        fontWeight: 500,
        borderRadius: '8px',
        padding: '10px 20px',
        marginRight: '12px',
      }}
      declineButtonStyle={{
        background: 'transparent',
        color: '#ffffff',
        fontSize: '14px',
        border: '2px solid #ffffff',
        borderRadius: '8px',
        padding: '10px 20px',
      }}
      expires={365}
      enableDeclineButton
      onAccept={handleAccept}
      onDecline={handleDecline}
      overlay={false}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, color: 'white' }}>
        <Box sx={{ fontSize: '15px', fontWeight: 500 }}>
          {t('cookieConsent.title', '🍪 We use cookies')}
        </Box>
        <Box sx={{ fontSize: '13px', opacity: 0.95, maxWidth: '800px' }}>
          {t(
            'cookieConsent.message',
            'We use cookies and similar technologies to enhance your experience, analyze traffic, and improve our services. By clicking "Accept", you consent to the use of cookies for analytics.'
          )}{' '}
          <Link
            href="#privacy"
            sx={{
              color: 'white',
              textDecoration: 'underline',
              '&:hover': { opacity: 0.8 },
            }}
          >
            {t('cookieConsent.learnMore', 'Learn more')}
          </Link>
        </Box>
      </Box>
    </CookieConsent>
  );
};
